<script setup>
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useCommonStore } from '~/common/stores/common.store.js';
import { useAssetMapping } from '~/project-management/composables/pm-asset-mapping.composable.js';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const emit = defineEmits(['close']);

const $t = inject('$t');
const $toast = inject('$toast');
const $services = inject('$services');

const route = useRoute();
const common_store = useCommonStore();
const project_management_store = useProjectManagementStore();
const { assets_custom_fields_map, assets_custom_fields } = storeToRefs(common_store);
const { active_task, active_schedule, is_fullscreen } = storeToRefs(project_management_store);
const { pm_activity_fields, getPossibleAssetFields } = useAssetMapping();

const state = reactive({
  form_data: {
    asset_mappings_list: [],
  },
  selected_asset_fields: [],
  selected_asset_fields_of_other_tasks_or_schedules: [],
  initial_data: [],
  is_loading: false,
  is_saving: false,
});

const add_update_and_delete = computed(() => {
  const original_list = state.initial_data;
  const new_list = state.form_data.asset_mappings_list;

  const add = [];
  const update = [];
  const deletes = [];

  new_list?.forEach?.((new_item) => {
    const original_item = original_list.find(item => item.field_uid === new_item.field_uid);
    if (!original_item) {
      add.push(new_item);
    }
    else if (original_item.field_uid !== new_item.field_uid
      || original_item.activity_uid !== new_item.activity_uid
      || original_item.properties !== new_item.properties
    ) {
      update.push(new_item);
    }
  });

  original_list.forEach((original_item) => {
    const new_item = new_list.find(item => item.field_uid === original_item.field_uid);
    if (!new_item)
      deletes.push(original_item);
  });

  const payload = {
    add,
    update,
    delete: deletes,
  };

  return payload;
});

const is_save_disabled = computed(() => {
  return (
    add_update_and_delete.value.add.length === 0
    || add_update_and_delete.value.add.some(item => !item.field_uid)
    || add_update_and_delete.value.add.some(item => !item.properties)
  ) && (
    add_update_and_delete.value.update.length === 0
    || add_update_and_delete.value.update.some(item => !item.field_uid)
    || add_update_and_delete.value.update.some(item => !item.properties)
  )
  && add_update_and_delete.value.delete.length === 0;
});

function addFieldMapping() {
  state.form_data.asset_mappings_list.push({
    properties: null,
    field_uid: '',
    activity_uid: active_task.value.uid,
  });
}

function removeMapping(index) {
  state.form_data.asset_mappings_list.splice(index, 1);
}

async function onSave() {
  state.is_saving = true;

  const payload = add_update_and_delete.value;
  try {
    const response = await $services.project_management.post({
      url: '/schedules',
      id: active_schedule.value.uid,
      attribute: 'asset-mapping',
      body: payload,
    });
    active_schedule.value.asset_mapping = response.data.data.asset_mapping;
    for (const asset_map_item of response.data.data.asset_mapping.configs) {
      assets_custom_fields_map.value[asset_map_item.field_uid] = {
        ...assets_custom_fields_map.value?.[asset_map_item.field_uid],
        uid: asset_map_item.field_uid,
        properties: {
          ...assets_custom_fields_map.value[asset_map_item.field_uid]?.properties,
          key: asset_map_item?.properties,
          activity_uid: asset_map_item?.activity_uid,
          schedule_uid: active_schedule?.value.uid,
        },
      };
    }

    for (const deleted_item of add_update_and_delete.value.delete) {
      if (assets_custom_fields_map.value[deleted_item.field_uid]) {
        delete assets_custom_fields_map.value[deleted_item.field_uid].properties.schedule_uid;
        delete assets_custom_fields_map.value[deleted_item.field_uid].properties.activity_uid;
      }
    }
  }
  catch (error) {
    $toast({
      title: error?.data?.title || $t('Something went wrong'),
      text: error?.data?.description || $t('Please try again'),
      type: 'error',
    });
  }
  finally {
    state.is_saving = false;
    emit('close');
  }
}

function updateSelectedValue(index, value) {
  state.selected_asset_fields[index] = value;
}

function resetFieldUid(index) {
  state.form_data.asset_mappings_list[index].field_uid = '';
}

onMounted(async () => {
  state.is_loading = true;
  const response = await $services.project_management.get({
    url: '/schedules',
    attribute: `asset-mapping/${route.params.asset_id}`,
  });
  state.initial_data = assets_custom_fields.value
    ?.map(item => item.uid);
  state.initial_data = active_schedule.value.asset_mapping?.configs?.filter?.((item) => {
    return state.initial_data.some(data => item.field_uid.includes(data));
  })?.filter?.(item => item.activity_uid === active_task.value?.uid) || [];
  state.form_data.asset_mappings_list = [...state.initial_data];

  state.selected_asset_fields = state.form_data.asset_mappings_list.map(item => item.field_uid);

  response.data.data.configs.forEach((config) => {
    const has_different_schedule_uid = !!config?.schedule_uid && config?.schedule_uid !== active_schedule.value?.uid;
    const has_different_activity_uid = !!config?.activity_uid && config?.activity_uid !== active_task.value?.uid;
    if (has_different_schedule_uid || has_different_activity_uid)
      state.selected_asset_fields_of_other_tasks_or_schedules.push(config.field_uid);
  });
  state.is_loading = false;
});
</script>

<template>
  <HawkModalContainer
    id="pm-field-mappings-modal-container"
    :options="{
      teleportTo: is_fullscreen
        ? '#pm-fullscreen-container'
        : 'body',
    }"
  >
    <Vueform
      v-model="state.form_data"
      size="sm"
      sync
      :display-errors="false"
      :display-messages="false"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            <div class="text-lg font-semibold text-gray-900">
              {{ $t('Sync properties') }}
            </div>
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <HawkLoader v-if="state.is_loading" />
          <template v-else>
            <div class="text-sm font-normal text-gray-600 mb-4">
              {{ $t('pm-sync-properties-text-1') }}
              <br>
              <br>
              <span class="font-semibold text-gray-800">
                {{ $t('Note') }}:
              </span>
              {{ $t('pm-sync-properties-text-2') }}
            </div>
            <div
              v-if="state.form_data.asset_mappings_list?.length"
              class="flex gap-[52px] text-sm font-medium text-gray-700 mb-1"
            >
              <div class="w-60">
                {{ $t('Schedule property') }}
              </div>
              <div class="w-56">
                {{ $t('Asset property') }}
              </div>
            </div>
            <ListElement
              name="asset_mappings_list"
              :add-classes="{ ListElement: { list: ['!gap-0'] } }"
              :controls="{ add: false, remove: false, sort: false }"
            >
              <template #default="{ index }">
                <ObjectElement
                  v-if="state.form_data.asset_mappings_list?.[index]?.properties !== undefined"
                  :name="index"
                  class="mb-3"
                >
                  <div class="flex w-full col-span-12">
                    <SelectElement
                      name="properties"
                      :items="pm_activity_fields"
                      :search="true"
                      :native="false"
                      :can-clear="false"
                      input-type="search"
                      autocomplete="off"
                      append-to="#pm-field-mappings-modal-container"
                      class="w-60"
                      @select="resetFieldUid(index)"
                    />
                    <div class="flex items-center h-full mx-4">
                      <IconHawkArrowRight class="text-gray-600" />
                    </div>
                    <SelectElement
                      name="field_uid"
                      :items="getPossibleAssetFields(state.form_data.asset_mappings_list, state.selected_asset_fields, state.selected_asset_fields_of_other_tasks_or_schedules)[index]"
                      value-prop="uid"
                      :search="true"
                      :native="false"
                      :can-clear="false"
                      input-type="search"
                      autocomplete="off"
                      append-to="#pm-field-mappings-modal-container"
                      class="w-56"
                      @change="updateSelectedValue(index, $event)"
                    />
                    <div
                      class="flex items-center h-full ml-4 cursor-pointer"
                      @click="removeMapping(index)"
                    >
                      <IconHawkTrashThree class="text-gray-600" />
                    </div>
                  </div>
                </ObjectElement>
              </template>
            </ListElement>
            <HawkButton
              type="text"
              @click="addFieldMapping"
            >
              <IconHawkPlus class="text-gray-600" />
              <span class="text-gray-600">
                {{ $t('New property') }}
              </span>
            </HawkButton>
          </template>
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end w-full col-span-full">
              <ButtonElement
                name="cancel"
                class="mr-4"
                :secondary="true"
                @click="emit('close')"
              >
                {{ $t('Cancel') }}
              </ButtonElement>
              <ButtonElement
                name="save"
                :disabled="is_save_disabled"
                :loading="state.is_saving"
                @click="onSave"
              >
                {{ $t('Save') }}
              </ButtonElement>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
